import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OfficeLayout from '../../components/OfficeLayout';
import { KPICard } from '../../components/KPICard';
import { StatusBadge } from '../../components/StatusBadge';
import { getDashboardStats, getRecords } from '../../utils/api';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { Plus, ClipboardList, Zap, CheckCircle } from 'lucide-react';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentRecords, setRecentRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useAutoRefresh(async () => {
    const [statsRes, recordsRes] = await Promise.all([getDashboardStats(), getRecords({})]);
    setStats(statsRes.data);
    setRecentRecords(recordsRes.data.slice(0, 15));
    setLoading(false);
  }, 30000); // 30 second refresh

  const getRefNumber = (r) =>
    r.document_type === 'trip_sheet'
      ? r.trip_sheet_fields?.trip_sheet_no || '—'
      : r.waybill_fields?.waybill_no || '—';

  if (loading) {
    return (
      <OfficeLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#0D9488] border-t-transparent"></div>
        </div>
      </OfficeLayout>
    );
  }

  return (
    <OfficeLayout>
      <div data-testid="office-dashboard">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-[#1A1A2E]">Operations Dashboard</h2>
            <p className="text-sm text-[#6B7280] mt-1">Monitor and manage all delivery operations</p>
          </div>
          <button
            onClick={() => navigate('/create-record')}
            className="flex items-center gap-2 bg-[#0D9488] hover:bg-[#0F766E] text-white px-5 py-2.5 rounded-lg font-medium text-sm transition-colors"
            data-testid="new-record-button"
          >
            <Plus size={18} /> New Record
          </button>
        </div>

        {/* KPI Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <KPICard title="Created Today" value={stats?.created_today || 0} color="orange" subtitle="New records" icon={<ClipboardList size={22} />} />
          <KPICard title="Allocated" value={stats?.allocated_jobs || 0} color="teal" subtitle="Assigned to drivers" icon={<Zap size={22} />} />
          <KPICard title="Completed Today" value={stats?.completed_today || 0} color="green" subtitle="Delivered" icon={<CheckCircle size={22} />} />
        </div>

        {/* Recent Records Table */}
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-base font-semibold text-[#1A1A2E]">Recent Records</h3>
            <button onClick={() => navigate('/active-records')} className="text-sm text-[#0D9488] hover:text-[#0F766E] font-medium">View All</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#6B7280]">Ref No.</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#6B7280]">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#6B7280]">3rd Party Agent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#6B7280]">Hub</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#6B7280]">Driver</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#6B7280]">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#6B7280]">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#6B7280]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentRecords.length === 0 ? (
                  <tr><td colSpan="8" className="px-6 py-12 text-center text-[#6B7280]">No records found. Create your first delivery record.</td></tr>
                ) : (
                  recentRecords.map((record) => (
                    <tr key={record.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-3.5 font-medium text-[#1A1A2E]">{getRefNumber(record)}</td>
                      <td className="px-6 py-3.5 text-[#6B7280] capitalize">{record.document_type.replace('_', ' ')}</td>
                      <td className="px-6 py-3.5 text-[#1A1A2E]">{record.third_party_agent}</td>
                      <td className="px-6 py-3.5 text-[#6B7280]">{record.dispatching_hub}</td>
                      <td className="px-6 py-3.5 text-[#6B7280]">{record.allocated_driver_name || '—'}</td>
                      <td className="px-6 py-3.5"><StatusBadge status={record.status} /></td>
                      <td className="px-6 py-3.5 text-[#6B7280]">{new Date(record.created_at).toLocaleDateString()}</td>
                      <td className="px-6 py-3.5">
                        <button onClick={() => navigate(`/record/${record.id}`)} className="text-[#0D9488] hover:text-[#0F766E] font-medium text-sm" data-testid={`view-record-${record.id}`}>View</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </OfficeLayout>
  );
}
